import { type CustomCellRendererProps } from "ag-grid-react";

import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { CounterBadge } from "@vega-core/components/CounterBadge/CounterBadge";
import { useColumnToggle } from "@vega-core/components/DataGrid/useColumnToggle";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";

import {
  deriveExportColumns,
  type ExportableColDef,
  toGridColumns,
} from "../../../hooks/deriveExportColumns";
import { RejectDetailsTooltip } from "../../TrackingTable/components/RejectDetailsTooltip";
import {
  formatGpEstimatedTicketSizeMaxCellText,
  formatGpEstimatedTicketSizeMinCellText,
  formatLimitedPartnerCellText,
  formatStageCellText,
  formatTierCellText,
} from "../../TrackingTable/utils/formatCellContent";

export const TRACK_ANALYTICS_COL_IDS = {
  limitedPartnerName: "limitedPartnerName",
  stage: "stage",
  tier: "tier",
  estimatedTicketSizeMin: "estimatedTicketSizeMin",
  estimatedTicketSizeMax: "estimatedTicketSizeMax",
  organizationChannel: "organizationChannel",
} as const;

export const useTrackAnalyticsColumns = () => {
  const limitedPartnerNameColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ANALYTICS_COL_IDS.limitedPartnerName,
      headerName: m.coInvestments_tracking_limitedPartner(),
      field: "limitedPartnerName",
      width: 240,
      pinned: "left",
      exportValue: formatLimitedPartnerCellText,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <span
            className="body-base-medium"
            data-testid={`cell-${data.dealOpportunityId}-limitedPartnerName`}
          >
            {data.limitedPartnerName}
          </span>
        );
      },
    };

  const stageColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_ANALYTICS_COL_IDS.stage,
    headerName: m.coInvestments_tracking_stage(),
    field: "stage",
    width: 140,
    toggleable: true,
    exportValue: formatStageCellText,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <RejectDetailsTooltip
          gpRejectReason={data.gpRejectReason}
          gpRejectSubreason={data.gpRejectSubreason}
          rejectComment={data.rejectComment}
          rejectReason={data.rejectReason}
        >
          <span className="body-base-regular">{formatStageCellText(data)}</span>
        </RejectDetailsTooltip>
      );
    },
  };

  const tierColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_ANALYTICS_COL_IDS.tier,
    headerName: m.coInvestments_tracking_tier(),
    field: "tier",
    width: 100,
    toggleable: true,
    exportValue: formatTierCellText,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <CounterBadge
          className="bg-background/tertiary"
          count={data.tier}
          variant="rounded"
        />
      );
    },
  };

  const estimatedTicketSizeMinColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ANALYTICS_COL_IDS.estimatedTicketSizeMin,
      headerName: m.coInvestments_tracking_gpMinimumTicketSize(),
      width: 160,
      toggleable: true,
      exportValue: formatGpEstimatedTicketSizeMinCellText,
      valueGetter: (params) =>
        params.data?.estimatedTicketSizeMin?.amount ?? null,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <span
            data-testid={`cell-${data.dealOpportunityId}-${TRACK_ANALYTICS_COL_IDS.estimatedTicketSizeMin}`}
          >
            {formatGpEstimatedTicketSizeMinCellText(data)}
          </span>
        );
      },
    };

  const estimatedTicketSizeMaxColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ANALYTICS_COL_IDS.estimatedTicketSizeMax,
      headerName: m.coInvestments_tracking_gpMaximumTicketSize(),
      width: 160,
      toggleable: true,
      exportValue: formatGpEstimatedTicketSizeMaxCellText,
      valueGetter: (params) =>
        params.data?.estimatedTicketSizeMax?.amount ?? null,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <span
            data-testid={`cell-${data.dealOpportunityId}-${TRACK_ANALYTICS_COL_IDS.estimatedTicketSizeMax}`}
          >
            {formatGpEstimatedTicketSizeMaxCellText(data)}
          </span>
        );
      },
    };

  const organizationChannelColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ANALYTICS_COL_IDS.organizationChannel,
      headerName: m.coInvestments_prospects_organizationChannel(),
      field: "organizationChannel",
      width: 160,
      toggleable: true,
      exportValue: (row) => row.organizationChannel || EMPTY_CELL_VALUE,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <span
            data-testid={`cell-${data.dealOpportunityId}-${TRACK_ANALYTICS_COL_IDS.organizationChannel}`}
          >
            {data.organizationChannel || EMPTY_CELL_VALUE}
          </span>
        );
      },
    };

  const columns: ExportableColDef<DealTrackingLimitedPartnerBreakdown>[] = [
    limitedPartnerNameColumn,
    stageColumn,
    tierColumn,
    estimatedTicketSizeMinColumn,
    estimatedTicketSizeMaxColumn,
    organizationChannelColumn,
  ];

  const { columns: exportColumns } = deriveExportColumns(columns);
  const { columnDefs, renderColumnToggle } = useColumnToggle(
    toGridColumns(columns),
  );

  return { columnDefs, renderColumnToggle, exportColumns };
};
